import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { track } from '@/analytics/analytics';
import { EXP_GOAL_UPSELL_MILESTONE, variantFor } from '@/analytics/experiments';
import { AmountKeypad } from '@/components/amount-keypad';
import { ConfettiBurst } from '@/components/confetti-burst';
import { goalIconOf } from '@/components/goal-icon-picker';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { IconTile } from '@/components/ui/icon-tile';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ProgressRing } from '@/components/ui/progress-ring';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useAppMetaStore } from '@/store/app-meta-store';
import { useAuthStore } from '@/store/auth-store';
import { savedGrOf, useGoalsStore } from '@/store/goals-store';
import { memberNameOf, useHouseholdStore } from '@/store/household-store';
import { colors, radius, spacing } from '@/theme/tokens';
import { formatPln, timeAgoPl } from '@/utils/format';
import { milestoneFor } from '@/utils/goal-milestones';

function parseAmountGr(raw: string): number {
  if (!raw) return 0;
  const [zl, gr = ''] = raw.split(',');
  const grPadded = gr.padEnd(2, '0').slice(0, 2);
  return (parseInt(zl || '0', 10) || 0) * 100 + (parseInt(grPadded || '0', 10) || 0);
}

/** Goal details — mock: szczeg_y_celu/screen.png (big ring, wpłaty, milestone card) */
export default function GoalDetailsScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const goals = useGoalsStore((s) => s.goals);
  const contributions = useGoalsStore((s) => s.contributions);
  const addContribution = useGoalsStore((s) => s.addContribution);
  const household = useHouseholdStore((s) => s.household);
  const account = useAuthStore((s) => s.account);
  const goalUpsellDismissed = useAppMetaStore((s) => s.goalUpsellDismissed);
  const goalHintDismissed = useAppMetaStore((s) => s.goalHintDismissed);
  const dismissGoalUpsell = useAppMetaStore((s) => s.dismissGoalUpsell);
  const dismissGoalHint = useAppMetaStore((s) => s.dismissGoalHint);

  const [entryOpen, setEntryOpen] = useState(false);
  const [raw, setRaw] = useState('');
  const [celebrating, setCelebrating] = useState(false);
  const [firstDone, setFirstDone] = useState(false);
  // "wow" rating ask on the very first contribution ever — fired AFTER the
  // confetti finishes so it doesn't cut the celebration short.
  const [rateAfterConfetti, setRateAfterConfetti] = useState(false);

  const goal = goals.find((g) => g.id === id);
  if (!goal) return null;

  const savedGr = savedGrOf(goal, contributions);
  const percent = Math.min(100, Math.round((savedGr / goal.targetGr) * 100));
  const allGoalContributions = contributions.filter((c) => c.goalId === goal.id);
  const goalContributions = allGoalContributions.slice(0, 5);
  const amountGr = parseAmountGr(raw);

  // Milestone upsell (A/B: first milestone 25% vs 50%) — only for goals with
  // real money on them, one dismissible card per crossed milestone.
  const variant = variantFor(EXP_GOAL_UPSELL_MILESTONE);
  const milestone = milestoneFor(
    goal.targetGr,
    savedGr,
    goalUpsellDismissed[goal.id] ?? [],
    variant === 'A' ? 25 : 50,
  );
  // "Nawet 50 zł" starter hint: only before the first contribution, dismissible.
  const showStarterHint = allGoalContributions.length === 0 && !goalHintDismissed.includes(goal.id);

  const onKey = (key: string) => {
    setRaw((prev) => {
      if (key === 'DEL') return prev.slice(0, -1);
      if (key === ',') {
        if (prev.includes(',') || prev === '') return prev;
        return `${prev},`;
      }
      const [, gr] = prev.split(',');
      if (gr !== undefined && gr.length >= 2) return prev;
      if (!prev.includes(',') && prev.replace(',', '').length >= 7) return prev;
      if (prev === '0' && key === '0') return prev;
      return prev + key;
    });
  };

  const onSaveContribution = async () => {
    const isFirst = allGoalContributions.length === 0;
    await addContribution(goal.id, amountGr, 'mem-0001');
    setRaw('');
    setEntryOpen(false);
    // Confetti on EVERY contribution (owner: "daj ją za każdym razem").
    setCelebrating(true);
    if (isFirst) {
      // First contribution also flips the one-off "pierwsza wpłata" card + hint.
      setFirstDone(true);
      // ...and it's a genuine "wow" moment — ask for a rating once, ever.
      if (!useAppMetaStore.getState().ratingDone) setRateAfterConfetti(true);
    }
  };

  // Upsell CTA: LINKED users go straight to chat with a prefilled ask; others
  // are routed to connect first (chat is for identified Polfinance clients).
  const onAskGuardian = () => {
    track('goal_upsell_cta', { source: variant });
    if (account?.linkStatus === 'LINKED') {
      router.push({
        pathname: '/chat',
        params: { draft: t('goalDetails.askDraft', { name: goal.name }) },
      });
    } else if (account) {
      router.push('/auth/connect');
    } else {
      router.push('/auth/login');
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader
          title={t('goalDetails.title')}
          rightIcon="pencil"
          onRightPress={() => router.push({ pathname: '/goal/edit', params: { id: goal.id } })}
        />

        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.sm }}>
          <Ionicons name={goalIconOf(goal)} size={24} color={colors.primary} />
          <AppText variant="headlineMd" style={{ textAlign: 'center' }}>
            {goal.name}
          </AppText>
        </View>

        {/* Big ring */}
        <View style={{ alignItems: 'center', gap: spacing.md }}>
          <ProgressRing percent={percent} size={200} strokeWidth={14}>
            <View style={{ alignItems: 'center', gap: spacing.base }}>
              <AppText variant="headlineMd" color={colors.navy}>
                {formatPln(savedGr)}
              </AppText>
              <AppText variant="bodyMd">
                {t('goalDetails.ofTarget', { target: formatPln(goal.targetGr) })}
              </AppText>
              <View
                style={{
                  backgroundColor: colors.primary,
                  borderRadius: radius.full,
                  paddingHorizontal: spacing.sm,
                  paddingVertical: 2,
                }}
              >
                <AppText variant="labelMd" color="#FFFFFF">
                  {t('goalDetails.percentOfGoal', { percent })}
                </AppText>
              </View>
            </View>
          </ProgressRing>
        </View>

        {!entryOpen && (
          <PrimaryButton label={`+ ${t('goalDetails.addContribution')}`} onPress={() => setEntryOpen(true)} />
        )}

        {/* First-contribution celebration (one-shot, dismissible) */}
        {firstDone && (
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.successBg }}>
            <IconTile icon="trophy-outline" bg="#7BE3A2" fg={colors.navy} size={44} />
            <View style={{ flex: 1, gap: 2 }}>
              <AppText variant="labelLg" color={colors.navy}>
                {t('goalDetails.firstContribTitle')}
              </AppText>
              <AppText variant="bodyMd">{t('goalDetails.firstContribBody')}</AppText>
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('common.cancel')}
              onPress={() => setFirstDone(false)}
              style={{ minWidth: 36, minHeight: 36, alignItems: 'center', justifyContent: 'center' }}
            >
              <Ionicons name="close" size={20} color={colors.outline} />
            </Pressable>
          </Card>
        )}

        {/* "Nawet 50 zł" starter hint — only before the first contribution */}
        {showStarterHint && !firstDone && (
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.infoBg }}>
            <IconTile icon="rocket-outline" bg={colors.primary} fg="#FFFFFF" size={44} />
            <View style={{ flex: 1, gap: 2 }}>
              <AppText variant="labelLg" color={colors.navy}>
                {t('goalDetails.starterHintTitle')}
              </AppText>
              <AppText variant="bodyMd">{t('goalDetails.starterHintBody')}</AppText>
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('common.cancel')}
              onPress={() => dismissGoalHint(goal.id)}
              style={{ minWidth: 36, minHeight: 36, alignItems: 'center', justifyContent: 'center' }}
            >
              <Ionicons name="close" size={20} color={colors.outline} />
            </Pressable>
          </Card>
        )}

        {/* Inline contribution entry (reuses the budget keypad) */}
        {entryOpen && (
          <Card style={{ gap: spacing.md }}>
            <AppText variant="headlineLg" color={colors.navy} style={{ textAlign: 'center' }}>
              + {raw === '' ? '0' : raw} zł
            </AppText>
            <AmountKeypad onKey={onKey} />
            <PrimaryButton
              label={amountGr > 0 ? `${t('goalDetails.saveContribution')} ${formatPln(amountGr)}` : t('goalDetails.saveContribution')}
              disabled={amountGr === 0}
              onPress={onSaveContribution}
            />
            <PrimaryButton label={t('common.cancel')} variant="secondary" onPress={() => { setEntryOpen(false); setRaw(''); }} />
          </Card>
        )}

        {/* Recent contributions */}
        {goalContributions.length > 0 && (
          <>
            <AppText variant="headlineSm">{t('goalDetails.recentContributions')}</AppText>
            <Card style={{ padding: 0 }}>
              {goalContributions.map((c, idx) => (
                <View
                  key={c.id}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: spacing.sm,
                    padding: spacing.md,
                    borderTopWidth: idx === 0 ? 0 : 1,
                    borderTopColor: colors.divider,
                  }}
                >
                  <IconTile icon="person-outline" bg={colors.infoBg} fg={colors.primary} size={44} />
                  <View style={{ flex: 1 }}>
                    <AppText variant="labelLg">
                      {memberNameOf(household, c.memberId)} — {formatPln(c.amountGr)}
                    </AppText>
                    <AppText variant="bodyMd">{timeAgoPl(c.createdAt)}</AppText>
                  </View>
                </View>
              ))}
            </Card>
          </>
        )}

        {/* Milestone upsell (mock: szczeg_y_celu blue card) — appears only once
            real money piled up (>=25/50/75% of a >=5000 zł goal, >=1000 zł saved),
            dismissible per milestone; never for small goals. */}
        {milestone !== null && (
          <UpsellCard
            variant={variant}
            onCta={onAskGuardian}
            ctaLabel={t('goalDetails.askGuardian')}
            onDismiss={() => {
              track('goal_upsell_dismissed', { source: variant, count: milestone });
              void dismissGoalUpsell(goal.id, milestone);
            }}
            body={t('goalDetails.milestoneBody', { saved: formatPln(savedGr) })}
          />
        )}
      </ScrollView>
      {celebrating && (
        <ConfettiBurst
          onDone={() => {
            setCelebrating(false);
            if (rateAfterConfetti) {
              setRateAfterConfetti(false);
              router.push('/rate');
            }
          }}
        />
      )}
    </SafeAreaView>
  );
}

/** The dismissible milestone card per the Stitch mock (blue, shield tile, X). */
function UpsellCard({
  variant,
  body,
  ctaLabel,
  onCta,
  onDismiss,
}: {
  variant: 'A' | 'B';
  body: string;
  ctaLabel: string;
  onCta: () => void;
  onDismiss: () => void;
}) {
  // Report one impression per mount (screen open with an active milestone).
  useEffect(() => {
    track('goal_upsell_shown', { source: variant });
    // once per mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View style={{ backgroundColor: colors.primary, borderRadius: radius.card, padding: spacing.md, gap: spacing.sm }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm }}>
        <IconTile icon="shield-checkmark-outline" bg="#4DA6F0" fg="#FFFFFF" size={44} />
        <AppText variant="bodyLg" color="#FFFFFF" style={{ flex: 1 }}>
          {body}
        </AppText>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Zamknij"
          onPress={onDismiss}
          style={{ minWidth: 36, minHeight: 36, alignItems: 'center', justifyContent: 'center' }}
        >
          <Ionicons name="close" size={20} color="#FFFFFF" />
        </Pressable>
      </View>
      <Pressable accessibilityRole="button" onPress={onCta} style={{ minHeight: 36, justifyContent: 'center' }}>
        <AppText variant="labelLg" color="#FFFFFF" style={{ textDecorationLine: 'underline' }}>
          {ctaLabel}
        </AppText>
      </Pressable>
    </View>
  );
}
